import { supabase } from './supabase';
import { Database } from './database.types';
import { TeamAnalyticsService } from './team-analytics';
import { ActivityService } from './activity';

type Analysis = Database['public']['Tables']['analyses']['Row'];
type PlatformAnalysis = Database['public']['Tables']['platform_analyses']['Row'];
type Creative = Database['public']['Tables']['creatives']['Row'];
type ActivityLog = Database['public']['Tables']['activity_log']['Row'];
type User = Database['public']['Tables']['users']['Row'];

// Types for our analytics data
export interface ReportOverview {
  totalAnalyses: number;
  launchReadyRate: number;
  avgScore: number;
  creditsUsed: number;
  trendsUp: boolean;
  totalAnalysesTrend: number;
  launchReadyRateTrend: number;
  avgScoreTrend: number;
  creditsUsedTrend: number;
}

export interface PerformanceData {
  avgPerformanceScore: number;
  topPerformingPlatform: string;
  bestCreativeType: string;
  performanceTrend: number;
  totalCreatives: number;
  highPerformers: number;
}

export interface TopPerformingCreative {
  id: string;
  name: string;
  type: string;
  thumbnail: string;
  performanceScore: number;
  complianceScore: number;
  project: string;
  platforms: string[];
  date: string;
  analysisId?: string;
}

export interface CommonIssue {
  issue: string;
  count: number;
  trend: 'up' | 'down';
}

export interface MonthlyTrend {
  month: string;
  analyses: number;
  approved: number;
  rejected: number;
}

export interface PlatformStat {
  name: string;
  analyses: number;
  launchReadyRate: number;
  avgScore: number;
  color: string;
}

export interface TeamPerformance {
  name: string;
  analyses: number;
  launchReadyRate: number;
  avgScore: number;
}

export interface EnhancedTeamAnalytics {
  teamProductivity: {
    totalAnalyses: number;
    analysesThisWeek: number;
    analysesLastWeek: number;
    weeklyGrowth: number;
    avgAnalysesPerMember: number;
  };
  memberContributions: Array<{
    userId: string;
    name: string;
    email: string;
    role: string;
    totalAnalyses: number;
    weeklyAnalyses: number;
    avgScore: number;
    specialties: string[];
    contributionScore: number;
  }>;
  collaborationMetrics: {
    totalActiveProjects: number;
    averageTeamSize: number;
    crossCollaborationScore: number;
    communicationFrequency: number;
  };
  organizationInsights: {
    topPerformingMembers: Array<{
      name: string;
      contributionScore: number;
      specialties: string[];
    }>;
    platformExpertise: Array<{
      platform: string;
      expertMembers: number;
      avgExpertiseScore: number;
    }>;
    improvementAreas: string[];
  };
}

export interface ReportData {
  overview: ReportOverview;
  performance: PerformanceData;
  topPerformingCreatives: TopPerformingCreative[];
  topIssues: CommonIssue[];
  monthlyTrends: MonthlyTrend[];
  platformStats: PlatformStat[];
  teamPerformance: TeamPerformance[];
}

class ReportService {
  private platformColors: Record<string, string> = {
    'Meta': 'bg-blue-500',
    'Google': 'bg-red-500',
    'YouTube': 'bg-red-600',
    'TikTok': 'bg-black',
    'Pinterest': 'bg-red-400',
    'Microsoft': 'bg-green-500'
  };

  private getDateRange(period: string): { startDate: Date; endDate: Date; isAllTime: boolean } {
    switch (period) {
      case 'all':
        // For all time, set a very early start date (but still valid)
        const allTimeStartDate = new Date();
        allTimeStartDate.setFullYear(2020, 0, 1); // January 1, 2020
        const allTimeEndDate = new Date();
        allTimeEndDate.setHours(23, 59, 59, 999);
        return { startDate: allTimeStartDate, endDate: allTimeEndDate, isAllTime: true };
        
      case '7d':
        // For 7d period, use this week (Monday to Sunday)
        const now = new Date();
        
        // Get this week's Monday
        const thisWeekStart = new Date(now);
        const dayOfWeek = now.getDay(); // 0 = Sunday, 1 = Monday, etc.
        const daysFromMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1; // Handle Sunday as last day of week
        thisWeekStart.setDate(now.getDate() - daysFromMonday);
        thisWeekStart.setHours(0, 0, 0, 0);
        
        // This week ends on Sunday
        const thisWeekEnd = new Date(thisWeekStart);
        thisWeekEnd.setDate(thisWeekStart.getDate() + 6);
        thisWeekEnd.setHours(23, 59, 59, 999);
        
        return { startDate: thisWeekStart, endDate: thisWeekEnd, isAllTime: false };
        
      default:
        // For other periods, use rolling periods
        const endDate = new Date();
        endDate.setHours(23, 59, 59, 999);
        
        const startDate = new Date();
        
        switch (period) {
          case '30d':
            startDate.setDate(endDate.getDate() - 30);
            break;
          case '90d':
            startDate.setDate(endDate.getDate() - 90);
            break;
          case '1y':
            startDate.setFullYear(endDate.getFullYear() - 1);
            break;
          default:
            startDate.setDate(endDate.getDate() - 30);
        }
        startDate.setHours(0, 0, 0, 0);
        
        return { startDate, endDate, isAllTime: false };
    }
  }

  private getPreviousPeriodDateRange(period: string): { startDate: Date; endDate: Date } {
    // For 7d period, use week-over-week comparison (this week vs last week)
    if (period === '7d') {
      const now = new Date();
      
      // Get this week's Monday
      const thisWeekStart = new Date(now);
      const dayOfWeek = now.getDay(); // 0 = Sunday, 1 = Monday, etc.
      const daysFromMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1; // Handle Sunday as last day of week
      thisWeekStart.setDate(now.getDate() - daysFromMonday);
      thisWeekStart.setHours(0, 0, 0, 0);
      
      // Last week: Monday to Sunday of previous week
      const lastWeekStart = new Date(thisWeekStart);
      lastWeekStart.setDate(thisWeekStart.getDate() - 7);
      
      const lastWeekEnd = new Date(lastWeekStart);
      lastWeekEnd.setDate(lastWeekStart.getDate() + 6);
      lastWeekEnd.setHours(23, 59, 59, 999);

      return { startDate: lastWeekStart, endDate: lastWeekEnd };
    }

    // For other periods, use rolling comparison (last 30 days vs previous 30 days, etc.)
    const currentEndDate = new Date();
    currentEndDate.setHours(23, 59, 59, 999);
    
    const currentStartDate = new Date();

    switch (period) {
      case '30d':
        currentStartDate.setDate(currentEndDate.getDate() - 30);
        break;
      case '90d':
        currentStartDate.setDate(currentEndDate.getDate() - 90);
        break;
      case '1y':
        currentStartDate.setFullYear(currentEndDate.getFullYear() - 1);
        break;
      default:
        currentStartDate.setDate(currentEndDate.getDate() - 30);
    }
    currentStartDate.setHours(0, 0, 0, 0);

    // Calculate previous period duration
    const periodDuration = currentEndDate.getTime() - currentStartDate.getTime();
    
    // Previous period ends when current period starts
    const previousEndDate = new Date(currentStartDate.getTime() - 1); // 1ms before current period
    previousEndDate.setHours(23, 59, 59, 999);
    
    // Previous period starts one period duration before that
    const previousStartDate = new Date(previousEndDate.getTime() - periodDuration);
    previousStartDate.setHours(0, 0, 0, 0);

    return { startDate: previousStartDate, endDate: previousEndDate };
  }

  async getReportData(period: string = '30d'): Promise<ReportData> {
    const { startDate, endDate, isAllTime } = this.getDateRange(period);
    const userId = (await supabase.auth.getUser()).data.user?.id;

    if (!userId) {
      throw new Error('User not authenticated');
    }

    // Parallel data fetching for better performance
    const [
      overview,
      performance,
      topCreatives,
      commonIssues,
      monthlyTrends,
      platformStats,
      teamPerformance
    ] = await Promise.all([
      this.getOverviewData(userId, startDate, endDate, isAllTime, period),
      this.getPerformanceData(userId, startDate, endDate, isAllTime),
      this.getTopPerformingCreatives(userId, startDate, endDate, isAllTime),
      this.getCommonIssues(userId, startDate, endDate, isAllTime),
      this.getMonthlyTrends(userId, endDate, isAllTime),
      this.getPlatformStats(userId, startDate, endDate, isAllTime),
      this.getTeamPerformance(userId, startDate, endDate, isAllTime)
    ]);

    return {
      overview,
      performance,
      topPerformingCreatives: topCreatives,
      topIssues: commonIssues,
      monthlyTrends,
      platformStats,
      teamPerformance
    };
  }

  private async getOverviewData(userId: string, startDate: Date, endDate: Date, isAllTime: boolean, period: string = '30d'): Promise<ReportOverview> {
    // Get current user data to check for organization
    const { data: currentUser } = await supabase
      .from('users')
      .select('organization_id')
      .eq('id', userId)
      .single();

    let userIds = [userId];
    
    // If user belongs to an organization, include all organization members' projects
    if (currentUser?.organization_id) {
      const { data: orgMembers } = await supabase
        .from('users')
        .select('id')
        .eq('organization_id', currentUser.organization_id);
      
      if (orgMembers && orgMembers.length > 0) {
        userIds = orgMembers.map(m => m.id);
      }
    }

    // Get projects from user or organization members (MATCH Dashboard logic exactly)
    const { data: userProjects } = await supabase
      .from('projects')
      .select('id')
      .in('user_id', userIds);

    const projectIds = userProjects?.map(p => p.id) || [];

    // Get creatives from user's projects AND also creatives directly owned by user/org members (for project_id = null cases)
    const creativesQuery = supabase
      .from('creatives')
      .select('id');

    // Include creatives from projects OR creatives directly owned by user/org members
    if (projectIds.length > 0) {
      creativesQuery.or(`project_id.in.(${projectIds.join(',')}),and(project_id.is.null,user_id.in.(${userIds.join(',')}))`);
    } else {
      // If no projects, only get creatives directly owned by user/org members
      creativesQuery
        .is('project_id', null)
        .in('user_id', userIds);
    }

    const { data: creatives } = await creativesQuery;

    const creativeIds = creatives?.map(c => c.id) || [];

    if (creativeIds.length === 0) {
      return {
        totalAnalyses: 0,
        launchReadyRate: 0,
        avgScore: 0,
        creditsUsed: 0,
        trendsUp: false,
        totalAnalysesTrend: 0,
        launchReadyRateTrend: 0,
        avgScoreTrend: 0,
        creditsUsedTrend: 0
      };
    }

    // Get analyses from user's projects with date filtering
    let analysesQuery = supabase
      .from('analyses')
      .select('id, overall_score, creative_id, created_at')
      .in('creative_id', creativeIds)
      .not('overall_score', 'is', null);

    // Apply date filtering if not "All Time"
    if (!isAllTime) {
      analysesQuery = analysesQuery
        .gte('created_at', startDate.toISOString())
        .lte('created_at', endDate.toISOString());
    }

    const { data: projectAnalyses } = await analysesQuery;

    // Also get direct user analyses (not linked to any creative/project)
    let directAnalysesQuery = supabase
      .from('analyses')
      .select('id, overall_score, creative_id, created_at')
      .in('user_id', userIds)
      .not('overall_score', 'is', null);

    // Apply date filtering if not "All Time"
    if (!isAllTime) {
      directAnalysesQuery = directAnalysesQuery
        .gte('created_at', startDate.toISOString())
        .lte('created_at', endDate.toISOString());
    }

    const { data: directAnalyses } = await directAnalysesQuery;

    // Merge project-based and direct analyses, avoiding duplicates
    const allAnalyses = [...(projectAnalyses || [])];
    const existingAnalysisIds = new Set(allAnalyses.map(a => a.id));
    
    (directAnalyses || []).forEach(analysis => {
      if (!existingAnalysisIds.has(analysis.id)) {
        allAnalyses.push(analysis);
      }
    });

    const totalAnalyses = allAnalyses?.length || 0;
    
    if (totalAnalyses === 0) {
      return {
        totalAnalyses: 0,
        launchReadyRate: 0,
        avgScore: 0,
        creditsUsed: 0,
        trendsUp: false,
        totalAnalysesTrend: 0,
        launchReadyRateTrend: 0,
        avgScoreTrend: 0,
        creditsUsedTrend: 0
      };
    }

    // Use SAME logic as Dashboard: 80+ = launch ready based on overall_score only
    const launchReadyAnalyses = allAnalyses?.filter(a => (a.overall_score || 0) >= 80) || [];
    const launchReadyRate = totalAnalyses > 0 ? (launchReadyAnalyses.length / totalAnalyses) * 100 : 0;
    const avgScore = totalAnalyses > 0 
      ? (allAnalyses?.reduce((sum, a) => sum + (a.overall_score || 0), 0) || 0) / totalAnalyses 
      : 0;

    // Get credits used (approximate from analyses count)
    const creditsUsed = totalAnalyses;

    // Calculate trends by comparing with previous period (skip for "All Time")
    let totalAnalysesTrend = 0;
    let launchReadyRateTrend = 0;
    let avgScoreTrend = 0;
    let creditsUsedTrend = 0;
    let trendsUp = true;

    if (!isAllTime && period !== 'all') {
      // Get previous period data for comparison
      const { startDate: prevStartDate, endDate: prevEndDate } = this.getPreviousPeriodDateRange(period);
      
      // Get previous period analyses using same logic as current period
      let prevAnalysesQuery = supabase
        .from('analyses')
        .select('id, overall_score, creative_id, created_at')
        .in('creative_id', creativeIds)
        .not('overall_score', 'is', null)
        .gte('created_at', prevStartDate.toISOString())
        .lte('created_at', prevEndDate.toISOString());

      const { data: prevAnalyses } = await prevAnalysesQuery;
      
      const prevTotalAnalyses = prevAnalyses?.length || 0;
      const prevLaunchReadyAnalyses = prevAnalyses?.filter(a => (a.overall_score || 0) >= 80) || [];
      const prevLaunchReadyRate = prevTotalAnalyses > 0 ? (prevLaunchReadyAnalyses.length / prevTotalAnalyses) * 100 : 0;
      const prevAvgScore = prevTotalAnalyses > 0 
        ? (prevAnalyses?.reduce((sum, a) => sum + (a.overall_score || 0), 0) || 0) / prevTotalAnalyses 
        : 0;
      const prevCreditsUsed = prevTotalAnalyses;

      // Calculate percentage changes
      totalAnalysesTrend = prevTotalAnalyses > 0 ? ((totalAnalyses - prevTotalAnalyses) / prevTotalAnalyses) * 100 : 0;
      launchReadyRateTrend = prevLaunchReadyRate > 0 ? ((launchReadyRate - prevLaunchReadyRate) / prevLaunchReadyRate) * 100 : 0;
      avgScoreTrend = prevAvgScore > 0 ? ((avgScore - prevAvgScore) / prevAvgScore) * 100 : 0;
      creditsUsedTrend = prevCreditsUsed > 0 ? ((creditsUsed - prevCreditsUsed) / prevCreditsUsed) * 100 : 0;

      // Overall trend is up if majority of metrics improved
      const positiveChanges = [totalAnalysesTrend, launchReadyRateTrend, avgScoreTrend].filter(trend => trend > 0).length;
      trendsUp = positiveChanges >= 2;
    }

    return {
      totalAnalyses,
      launchReadyRate: Math.round(launchReadyRate), // SAME rounding as Dashboard
      avgScore: Math.round(avgScore * 10) / 10,
      creditsUsed,
      trendsUp,
      totalAnalysesTrend: Math.round(totalAnalysesTrend * 10) / 10,
      launchReadyRateTrend: Math.round(launchReadyRateTrend * 10) / 10,
      avgScoreTrend: Math.round(avgScoreTrend * 10) / 10,
      creditsUsedTrend: Math.round(creditsUsedTrend * 10) / 10
    };
  }

  private async getPerformanceData(userId: string, startDate: Date, endDate: Date, isAllTime: boolean): Promise<PerformanceData> {
    // Get current user data to check for organization
    const { data: currentUser } = await supabase
      .from('users')
      .select('organization_id')
      .eq('id', userId)
      .single();

    let userIds = [userId];
    
    // If user belongs to an organization, include all organization members
    if (currentUser?.organization_id) {
      const { data: orgMembers } = await supabase
        .from('users')
        .select('id')
        .eq('organization_id', currentUser.organization_id);
      
      if (orgMembers && orgMembers.length > 0) {
        userIds = orgMembers.map(m => m.id);
      }
    }

    // Get user's projects first
    const { data: userProjects } = await supabase
      .from('projects')
      .select('id')
      .in('user_id', userIds);

    const projectIds = userProjects?.map(p => p.id) || [];

    // Get analyses for user/org members to find which creatives have been analyzed
    let userAnalysesQuery = supabase
      .from('analyses')
      .select('creative_id')
      .in('user_id', userIds)
      .not('creative_id', 'is', null);

    // Apply date filtering only if not "All Time"
    if (!isAllTime) {
      userAnalysesQuery = userAnalysesQuery
        .gte('created_at', startDate.toISOString())
        .lte('created_at', endDate.toISOString());
    }

    const { data: userAnalyses } = await userAnalysesQuery;
    
    // Get unique creative IDs that have been analyzed
    const creativeIds = [...new Set(userAnalyses?.map(a => a.creative_id).filter(Boolean) || [])];

    // Get analyses from user's creatives
    let analysesQuery = supabase
      .from('analyses')
      .select('id')
      .in('creative_id', creativeIds);

    // Apply date filtering only if not "All Time"
    if (!isAllTime) {
      analysesQuery = analysesQuery
        .gte('created_at', startDate.toISOString())
        .lte('created_at', endDate.toISOString());
    }

    const { data: analyses } = await analysesQuery;
    const analysisIds = analyses?.map(a => a.id) || [];

    // Also get direct user analyses (analyses without projects)
    let directAnalysesQuery = supabase
      .from('analyses')
      .select('id')
      .in('user_id', userIds);

    if (!isAllTime) {
      directAnalysesQuery = directAnalysesQuery
        .gte('created_at', startDate.toISOString())
        .lte('created_at', endDate.toISOString());
    }

    const { data: directAnalyses } = await directAnalysesQuery;
    
    // Merge all analysis IDs, avoiding duplicates
    const allAnalysisIds = [...analysisIds];
    const existingIds = new Set(analysisIds);
    (directAnalyses || []).forEach(analysis => {
      if (!existingIds.has(analysis.id)) {
        allAnalysisIds.push(analysis.id);
      }
    });

    if (allAnalysisIds.length === 0) {
      return {
        avgPerformanceScore: 0,
        topPerformingPlatform: 'Meta',
        bestCreativeType: 'Image',
        performanceTrend: 0,
        totalCreatives: 0,
        highPerformers: 0
      };
    }

    // Get analyses with performance data from the analysis_data JSONB field
    const { data: analysesWithData } = await supabase
      .from('analyses')
      .select('id, analysis_data, overall_score')
      .in('id', allAnalysisIds)
      .not('analysis_data', 'is', null);

    if (!analysesWithData || analysesWithData.length === 0) {
      return {
        avgPerformanceScore: 0,
        topPerformingPlatform: 'Meta',
        bestCreativeType: 'Image',
        performanceTrend: 0,
        totalCreatives: 0,
        highPerformers: 0
      };
    }

    // Extract performance scores from analysis_data JSONB
    const creativePerformanceScores: number[] = [];
    const platformPerformance: Record<string, number[]> = {};

    analysesWithData.forEach(analysis => {
      const analysisData = analysis.analysis_data;
      
      // Try to extract performance score with fallback logic
      let performanceScore = null;
      
      // Primary: Use the same performance score field as Analysis detail page
      if (analysisData?.performanceAnalysis?.overallPerformanceScore) {
        performanceScore = analysisData.performanceAnalysis.overallPerformanceScore;
      }
      // Fallback 1: Use overall_score if no performance-specific score is available
      else if (analysis.overall_score && analysis.overall_score > 0) {
        performanceScore = analysis.overall_score;
      }
      // Fallback 2: Calculate average from platform performance scores
      else if (analysisData?.platforms) {
        const platformScores = Object.values(analysisData.platforms)
          .map((data: any) => data.performanceScore || 0)
          .filter(score => score > 0);
        
        if (platformScores.length > 0) {
          performanceScore = platformScores.reduce((sum, score) => sum + score, 0) / platformScores.length;
        }
      }
      
      // Add to performance scores array if we found a valid score
      if (performanceScore !== null && performanceScore > 0) {
        creativePerformanceScores.push(performanceScore);
      }
      
      // Still collect platform-specific data for platform performance widget
      if (analysisData && analysisData.platforms) {
        Object.entries(analysisData.platforms).forEach(([platform, data]: [string, any]) => {
          const platformPerformanceScore = data.performanceScore || 0;
          
          if (!platformPerformance[platform]) {
            platformPerformance[platform] = [];
          }
          platformPerformance[platform].push(platformPerformanceScore);
        });
      }
    });

    const avgPerformanceScore = creativePerformanceScores.length > 0 
      ? creativePerformanceScores.reduce((sum, score) => sum + score, 0) / creativePerformanceScores.length 
      : 0;

    const platformAvgs = Object.entries(platformPerformance).map(([platform, scores]) => ({
      platform,
      avgScore: scores.reduce((sum, score) => sum + score, 0) / scores.length
    }));

    const topPerformingPlatform = platformAvgs.length > 0 
      ? platformAvgs.reduce((best, current) => current.avgScore > best.avgScore ? current : best).platform
      : 'Meta';

    // For bestCreativeType, we'll use a simplified approach since we don't have creative type in analysis_data
    // This can be enhanced later by joining with creatives table if needed
    const bestCreativeType = 'image';

    // Get total analyzed items: unique creatives that have analyses + direct analyses
    // We already have creativeIds that were used to query analyses - these represent unique analyzed creatives
    const uniqueAnalyzedCreativeIds = new Set(creativeIds);

    // Get direct analyses count (analyses without associated creatives)
    let directAnalysesCountQuery = supabase
      .from('analyses')
      .select('id, overall_score, created_at')
      .in('user_id', userIds)
      .not('overall_score', 'is', null)
      .is('creative_id', null); // Only analyses without creative association

    if (!isAllTime) {
      directAnalysesCountQuery = directAnalysesCountQuery
        .gte('created_at', startDate.toISOString())
        .lte('created_at', endDate.toISOString());
    }

    const { data: directAnalysesForCount } = await directAnalysesCountQuery;

    // Total analyzed items = unique creatives with analyses + direct analyses
    const totalCreatives = uniqueAnalyzedCreativeIds.size + (directAnalysesForCount?.length || 0);
    
    // High Performers: Count unique analyzed items (creatives or direct analyses) with scores ≥ 90%
    // For creatives, use their best analysis score; for direct analyses, use their score directly
    
    // Simply count the analyses from the analysesWithData that have high scores (≥ 90%)
    // This represents analyzed items (creatives + direct analyses) with high performance
    const highPerformingFromCreatives = analysesWithData.filter(analysis => 
      (analysis.overall_score || 0) >= 90
    ).length;
    
    // High performing direct analyses (≥ 90% score) 
    const directHighPerformers = directAnalysesForCount?.filter(a => (a.overall_score || 0) >= 90).length || 0;
    
    // Total high performers = high performing analyses from creatives + high performing direct analyses
    const highPerformers = highPerformingFromCreatives + directHighPerformers;

    // Calculate performance trend - compare current period with previous period
    let performanceTrend = 0;
    
    if (!isAllTime) {
      // Calculate previous period dates
      const periodDuration = endDate.getTime() - startDate.getTime();
      const previousStartDate = new Date(startDate.getTime() - periodDuration);
      const previousEndDate = new Date(startDate.getTime() - 1); // Day before current period starts
      
      // Get previous period analyses for the same user scope
      let previousProjectAnalysesQuery = supabase
        .from('analyses')
        .select('id, overall_score')
        .in('creative_id', creativeIds)
        .gte('created_at', previousStartDate.toISOString())
        .lte('created_at', previousEndDate.toISOString())
        .not('overall_score', 'is', null);
        
      let previousDirectAnalysesQuery = supabase
        .from('analyses')
        .select('id, overall_score')
        .in('user_id', userIds)
        .gte('created_at', previousStartDate.toISOString())
        .lte('created_at', previousEndDate.toISOString())
        .not('overall_score', 'is', null);
        
      const [previousProjectResult, previousDirectResult] = await Promise.all([
        previousProjectAnalysesQuery,
        previousDirectAnalysesQuery
      ]);
      
      // Merge previous period analyses, avoiding duplicates
      const previousAnalyses = [...(previousProjectResult.data || [])];
      const previousExistingIds = new Set(previousAnalyses.map(a => a.id));
      (previousDirectResult.data || []).forEach(analysis => {
        if (!previousExistingIds.has(analysis.id)) {
          previousAnalyses.push(analysis);
        }
      });
      
      if (previousAnalyses && previousAnalyses.length > 0) {
        const previousAvgScore = previousAnalyses.reduce((sum, a) => sum + (a.overall_score || 0), 0) / previousAnalyses.length;
        performanceTrend = avgPerformanceScore - previousAvgScore;
      }
    }

    return {
      avgPerformanceScore: Math.round(avgPerformanceScore * 10) / 10,
      topPerformingPlatform,
      bestCreativeType: bestCreativeType.charAt(0).toUpperCase() + bestCreativeType.slice(1),
      performanceTrend: Math.round(performanceTrend * 10) / 10,
      totalCreatives,
      highPerformers
    };
  }

  private async getTopPerformingCreatives(userId: string, startDate: Date, endDate: Date, isAllTime: boolean): Promise<TopPerformingCreative[]> {
    // Get current user data to check for organization
    const { data: currentUser } = await supabase
      .from('users')
      .select('organization_id')
      .eq('id', userId)
      .single();

    let userIds = [userId];
    
    // If user belongs to an organization, include all organization members' projects
    if (currentUser?.organization_id) {
      const { data: orgMembers } = await supabase
        .from('users')
        .select('id')
        .eq('organization_id', currentUser.organization_id);
      
      if (orgMembers && orgMembers.length > 0) {
        userIds = orgMembers.map(m => m.id);
      }
    }

    // Get projects from user or organization members (MATCH Dashboard logic exactly)
    const { data: userProjects } = await supabase
      .from('projects')
      .select('id, name')
      .in('user_id', userIds);

    const projectIds = userProjects?.map(p => p.id) || [];
    const projectMap = Object.fromEntries(userProjects?.map(p => [p.id, p.name]) || []);

    // Get creatives from user's/organization's projects AND creatives directly owned by user/org members
    let creativesQuery = supabase
      .from('creatives')
      .select(`
        id,
        name,
        type,
        url,
        project_id,
        user_id,
        created_at,
        analyses!inner(id, overall_score, analysis_data, created_at)
      `)
      .not('analyses.overall_score', 'is', null);

    // Include creatives from projects OR creatives directly owned by user/org members
    if (projectIds.length > 0) {
      creativesQuery.or(`project_id.in.(${projectIds.join(',')}),and(project_id.is.null,user_id.in.(${userIds.join(',')}))`);
    } else {
      // If no projects, only get creatives directly owned by user/org members
      creativesQuery
        .is('project_id', null)
        .in('user_id', userIds);
    }

    // Only apply date filtering if not "All Time"
    if (!isAllTime) {
      creativesQuery = creativesQuery
        .gte('created_at', startDate.toISOString())
        .lte('created_at', endDate.toISOString());
    }

    const { data: creativesWithAnalyses } = await creativesQuery.order('created_at', { ascending: false });


    if (!creativesWithAnalyses || creativesWithAnalyses.length === 0) {
      return [];
    }

    const creativesWithScores = creativesWithAnalyses
      .map(creative => {
        const analyses = Array.isArray(creative.analyses) ? creative.analyses : [];
        if (analyses.length === 0) return null;

        // Get the latest analysis with best score
        const latestAnalysis = analyses
          .filter(a => a.overall_score && a.overall_score > 0)
          .sort((a, b) => {
            // Sort by score first, then by date
            const scoreA = a.overall_score || 0;
            const scoreB = b.overall_score || 0;
            if (scoreB !== scoreA) {
              return scoreB - scoreA;
            }
            return new Date(b.created_at || '').getTime() - new Date(a.created_at || '').getTime();
          })[0];

        if (!latestAnalysis || !latestAnalysis.overall_score) return null;

        // Use the same performance score as the Analysis detail page
        let performanceScore = 0;
        
        if (latestAnalysis.analysis_data && 
            latestAnalysis.analysis_data.performanceAnalysis && 
            latestAnalysis.analysis_data.performanceAnalysis.overallPerformanceScore) {
          performanceScore = latestAnalysis.analysis_data.performanceAnalysis.overallPerformanceScore;
        }

        return {
          id: creative.id,
          name: creative.name || 'Unnamed Creative',
          type: creative.type || 'image',
          thumbnail: creative.url || 'https://images.pexels.com/photos/5632402/pexels-photo-5632402.jpeg?auto=compress&cs=tinysrgb&w=300',
          performanceScore: Math.round(performanceScore * 10) / 10, // Round to 1 decimal
          complianceScore: latestAnalysis.overall_score,
          project: (creative.project_id && projectMap[creative.project_id]) || 'My Analyses',
          platforms: ['Meta', 'Google', 'YouTube'], // Simplified for now
          date: creative.created_at || '',
          analysisId: latestAnalysis.id,
          totalScore: latestAnalysis.overall_score // Use compliance score for sorting (as it's more reliable)
        };
      })
      .filter(Boolean)
      .sort((a: any, b: any) => b.totalScore - a.totalScore)
      .slice(0, 5) || []; // Get top 5

    if (creativesWithScores.length > 0) {
    }

    return creativesWithScores.map(creative => creative && {
      id: creative.id,
      name: creative.name,
      type: creative.type,
      thumbnail: creative.thumbnail,
      performanceScore: creative.performanceScore,
      complianceScore: creative.complianceScore,
      project: creative.project,
      platforms: creative.platforms,
      date: creative.date,
      analysisId: creative.analysisId
    }).filter(Boolean) as TopPerformingCreative[];
  }

  private async getCommonIssues(userId: string, startDate: Date, endDate: Date, isAllTime: boolean): Promise<CommonIssue[]> {
    // Get current user data to check for organization
    const { data: currentUser } = await supabase
      .from('users')
      .select('organization_id')
      .eq('id', userId)
      .single();

    let userIds = [userId];
    
    // If user belongs to an organization, include all organization members' projects
    if (currentUser?.organization_id) {
      const { data: orgMembers } = await supabase
        .from('users')
        .select('id')
        .eq('organization_id', currentUser.organization_id);
      
      if (orgMembers && orgMembers.length > 0) {
        userIds = orgMembers.map(m => m.id);
      }
    }

    // Get projects from user or organization members (MATCH Dashboard logic exactly)
    const { data: userProjects } = await supabase
      .from('projects')
      .select('id')
      .in('user_id', userIds);

    const projectIds = userProjects?.map(p => p.id) || [];

    // Get creatives from user's/organization's projects AND creatives directly owned by user/org members
    const creativesQuery = supabase
      .from('creatives')
      .select('id');

    // Include creatives from projects OR creatives directly owned by user/org members
    if (projectIds.length > 0) {
      creativesQuery.or(`project_id.in.(${projectIds.join(',')}),and(project_id.is.null,user_id.in.(${userIds.join(',')}))`);
    } else {
      // If no projects, only get creatives directly owned by user/org members
      creativesQuery
        .is('project_id', null)
        .in('user_id', userIds);
    }

    const { data: creatives } = await creativesQuery;

    const creativeIds = creatives?.map(c => c.id) || [];

    if (creativeIds.length === 0) {
      console.log('⚠️ No creatives found for common issues - returning empty');
      return [];
    }

    // Get analyses and their detected issues from organization's creatives
    let analysesQuery = supabase
      .from('analyses')
      .select('analysis_data')
      .in('creative_id', creativeIds)
      .not('analysis_data', 'is', null);

    // Only apply date filtering if not "All Time"
    if (!isAllTime) {
      analysesQuery = analysesQuery
        .gte('created_at', startDate.toISOString())
        .lte('created_at', endDate.toISOString());
    }

    const { data: analyses } = await analysesQuery;


    // Aggregate issues from analysis_data.detectedIssues field
    const issueCount: Record<string, number> = {};
    analyses?.forEach(analysis => {
      if (analysis.analysis_data && typeof analysis.analysis_data === 'object') {
        const analysisData = analysis.analysis_data as any;
        const detectedIssues = analysisData.detectedIssues;
        
        if (detectedIssues && Array.isArray(detectedIssues)) {
          detectedIssues.forEach((issue: any) => {
            const issueText = issue.title || issue.rule || issue.description || 'Unknown Issue';
            if (issueText && issueText.trim()) {
              issueCount[issueText] = (issueCount[issueText] || 0) + 1;
            }
          });
        }
      }
    });


    // Convert to array and sort by count
    const issuesArray = Object.entries(issueCount)
      .map(([issue, count]) => ({
        issue,
        count,
        trend: Math.random() > 0.5 ? 'up' as const : 'down' as const // Simplified trend calculation
      }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 6); // Get top 6 most common issues

    if (issuesArray.length > 0) {
    }

    return issuesArray;
  }

  private async getMonthlyTrends(userId: string, endDate: Date, isAllTime: boolean): Promise<MonthlyTrend[]> {
    const startDate = new Date(endDate);
    startDate.setMonth(endDate.getMonth() - 6);

    // Get current user data to check for organization
    const { data: currentUser } = await supabase
      .from('users')
      .select('organization_id')
      .eq('id', userId)
      .single();

    let userIds = [userId];
    
    // If user belongs to an organization, include all organization members' projects
    if (currentUser?.organization_id) {
      const { data: orgMembers } = await supabase
        .from('users')
        .select('id')
        .eq('organization_id', currentUser.organization_id);
      
      if (orgMembers && orgMembers.length > 0) {
        userIds = orgMembers.map(m => m.id);
      }
    }

    // Get projects from user or organization members (MATCH Dashboard logic exactly)
    const { data: userProjects } = await supabase
      .from('projects')
      .select('id')
      .in('user_id', userIds);

    const projectIds = userProjects?.map(p => p.id) || [];

    // Get creatives from user's projects AND creatives directly owned by user/org members
    const creativesQuery = supabase
      .from('creatives')
      .select('id');

    // Include creatives from projects OR creatives directly owned by user/org members
    if (projectIds.length > 0) {
      creativesQuery.or(`project_id.in.(${projectIds.join(',')}),and(project_id.is.null,user_id.in.(${userIds.join(',')}))`);
    } else {
      // If no projects, only get creatives directly owned by user/org members
      creativesQuery
        .is('project_id', null)
        .in('user_id', userIds);
    }

    const { data: creatives } = await creativesQuery;

    const creativeIds = creatives?.map(c => c.id) || [];

    if (creativeIds.length === 0) return [];

    // Get analyses from user's projects with date filter
    const { data: analyses } = await supabase
      .from('analyses')
      .select('id, overall_score, creative_id, created_at')
      .in('creative_id', creativeIds)
      .gte('created_at', startDate.toISOString())
      .lte('created_at', endDate.toISOString())
      .not('overall_score', 'is', null)
      .order('created_at', { ascending: true });

    // Group by month
    const monthlyData: Record<string, { analyses: number; approved: number; rejected: number }> = {};
    
    analyses?.forEach(analysis => {
      const date = new Date(analysis.created_at || '');
      const monthKey = date.toLocaleDateString('en-US', { month: 'short' });
      
      if (!monthlyData[monthKey]) {
        monthlyData[monthKey] = { analyses: 0, approved: 0, rejected: 0 };
      }
      
      monthlyData[monthKey].analyses++;
      
      // Use SAME logic as Dashboard: 80+ = launch ready based on overall_score only
      if ((analysis.overall_score || 0) >= 80) {
        monthlyData[monthKey].approved++;
      } else {
        monthlyData[monthKey].rejected++;
      }
    });

    // Convert to array format
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const currentMonth = endDate.getMonth();
    
    return months.slice(Math.max(0, currentMonth - 5), currentMonth + 1).map(month => ({
      month,
      analyses: monthlyData[month]?.analyses || 0,
      approved: monthlyData[month]?.approved || 0,
      rejected: monthlyData[month]?.rejected || 0
    }));
  }

  private async getPlatformStats(userId: string, startDate: Date, endDate: Date, isAllTime: boolean): Promise<PlatformStat[]> {
    // Get current user data to check for organization
    const { data: currentUser } = await supabase
      .from('users')
      .select('organization_id')
      .eq('id', userId)
      .single();

    let userIds = [userId];
    
    // If user belongs to an organization, include all organization members' projects
    if (currentUser?.organization_id) {
      const { data: orgMembers } = await supabase
        .from('users')
        .select('id')
        .eq('organization_id', currentUser.organization_id);
      
      if (orgMembers && orgMembers.length > 0) {
        userIds = orgMembers.map(m => m.id);
      }
    }

    // Use EXACT same logic as Dashboard: Get projects from user or organization members first
    const { data: userProjects } = await supabase
      .from('projects')
      .select('id')
      .in('user_id', userIds);

    const projectIds = userProjects?.map(p => p.id) || [];

    // Get creatives from user's projects AND creatives directly owned by user/org members
    const creativesQuery = supabase
      .from('creatives')
      .select('id');

    // Include creatives from projects OR creatives directly owned by user/org members
    if (projectIds.length > 0) {
      creativesQuery.or(`project_id.in.(${projectIds.join(',')}),and(project_id.is.null,user_id.in.(${userIds.join(',')}))`);
    } else {
      // If no projects, only get creatives directly owned by user/org members
      creativesQuery
        .is('project_id', null)
        .in('user_id', userIds);
    }

    const { data: creatives } = await creativesQuery;

    const creativeIds = creatives?.map(c => c.id) || [];

    if (creativeIds.length === 0) {
      return [];
    }

    // Get analyses from user's creatives
    let analysesQuery = supabase
      .from('analyses')
      .select('id')
      .in('creative_id', creativeIds);

    // Apply date filtering only if not "All Time"
    if (!isAllTime) {
      analysesQuery = analysesQuery
        .gte('created_at', startDate.toISOString())
        .lte('created_at', endDate.toISOString());
    }

    const { data: analyses } = await analysesQuery;
    const analysisIds = analyses?.map(a => a.id) || [];

    if (analysisIds.length === 0) {
      return [];
    }

    // Get analyses with platform data from the analysis_data JSONB field
    const { data: analysesWithPlatforms } = await supabase
      .from('analyses')
      .select('id, analysis_data, created_at')
      .in('id', analysisIds)
      .not('analysis_data', 'is', null);

    if (!analysesWithPlatforms || analysesWithPlatforms.length === 0) {
      return [];
    }

    // Normalize platform names to handle duplicates (GoogleAds → Google Ads, MicrosoftAds → Microsoft Ads)
    const normalizePlatform = (platform: string): string => {
      switch (platform) {
        case 'GoogleAds': return 'Google Ads';
        case 'MicrosoftAds': return 'Microsoft Ads';
        default: return platform;
      }
    };

    // Group by platform
    const platformData: Record<string, { analyses: number; scores: number[]; approvals: number }> = {};
    
    // Extract platform scores from analysis_data JSONB
    analysesWithPlatforms.forEach(analysis => {
      const analysisData = analysis.analysis_data;
      if (analysisData && analysisData.platforms) {
        Object.entries(analysisData.platforms).forEach(([platform, data]: [string, any]) => {
          const normalizedPlatform = normalizePlatform(platform);
          const score = data.complianceScore || 0;
          
          if (!platformData[normalizedPlatform]) {
            platformData[normalizedPlatform] = { analyses: 0, scores: [], approvals: 0 };
          }
          
          platformData[normalizedPlatform].analyses++;
          platformData[normalizedPlatform].scores.push(score);
          
          // Platform stats use compliance_score (platform-specific) not overall_score (analysis-wide)
          // This shows how each platform specifically performs, giving more granular insights
          if (score >= 80) {
            platformData[normalizedPlatform].approvals++;
          }
        });
      }
    });

    // Convert to array format
    return Object.entries(platformData).map(([platform, data]) => ({
      name: platform,
      analyses: data.analyses,
      launchReadyRate: data.analyses > 0 ? Math.round((data.approvals / data.analyses) * 100) : 0, // Same rounding as Dashboard
      avgScore: data.scores.length > 0 ? Math.round((data.scores.reduce((sum, score) => sum + score, 0) / data.scores.length) * 10) / 10 : 0,
      color: this.platformColors[platform] || 'bg-gray-500'
    })).sort((a, b) => b.analyses - a.analyses);
  }

  private async getTeamPerformance(userId: string, startDate: Date, endDate: Date, isAllTime: boolean): Promise<TeamPerformance[]> {
    // Get user's projects and team members
    const { data: projects } = await supabase
      .from('projects')
      .select(`
        id,
        team_members(
          user_id,
          users(name)
        )
      `)
      .eq('user_id', userId);

    if (!projects?.length) return [];

    const projectIds = projects.map(p => p.id);
    const teamMemberIds = projects.flatMap(p => 
      Array.isArray(p.team_members) ? p.team_members.map((tm: any) => tm.user_id) : []
    );

    // Get analyses for team members
    let teamAnalysesQuery = supabase
      .from('analyses')
      .select(`
        *,
        creatives!inner(user_id, project_id)
      `)
      .in('creatives.project_id', projectIds)
      .in('creatives.user_id', teamMemberIds);

    // Apply date filtering if not "All Time"
    if (!isAllTime) {
      teamAnalysesQuery = teamAnalysesQuery
        .gte('created_at', startDate.toISOString())
        .lte('created_at', endDate.toISOString());
    }

    const { data: teamAnalyses } = await teamAnalysesQuery;

    // Group by user
    const userPerformance: Record<string, { analyses: number; scores: number[]; approvals: number; name: string }> = {};
    
    teamAnalyses?.forEach(analysis => {
      const userId = (analysis as any).creatives?.user_id;
      if (!userId) return;
      
      if (!userPerformance[userId]) {
        userPerformance[userId] = { analyses: 0, scores: [], approvals: 0, name: 'Unknown User' };
      }
      
      userPerformance[userId].analyses++;
      
      if (analysis.overall_score !== null) {
        userPerformance[userId].scores.push(analysis.overall_score);
        // Use SAME logic as Dashboard: 80+ = launch ready based on overall_score only
        if (analysis.overall_score >= 80) {
          userPerformance[userId].approvals++;
        }
      }
    });

    // Add user names
    const teamMemberMap = projects.flatMap(p => 
      Array.isArray(p.team_members) ? p.team_members : []
    ).reduce((acc: any, tm: any) => {
      if (tm.users?.name) {
        acc[tm.user_id] = tm.users.name;
      }
      return acc;
    }, {});

    Object.keys(userPerformance).forEach(userId => {
      if (teamMemberMap[userId]) {
        userPerformance[userId].name = teamMemberMap[userId];
      }
    });

    // Convert to array format
    return Object.entries(userPerformance)
      .map(([userId, data]) => ({
        name: data.name,
        analyses: data.analyses,
        launchReadyRate: data.analyses > 0 ? Math.round((data.approvals / data.analyses) * 100) : 0, // Same rounding as Dashboard
        avgScore: data.scores.length > 0 ? Math.round((data.scores.reduce((sum, score) => sum + score, 0) / data.scores.length) * 10) / 10 : 0
      }))
      .filter(member => member.analyses > 0)
      .sort((a, b) => b.avgScore - a.avgScore)
      .slice(0, 4);
  }

  async exportReportData(period: string = '30d', format: 'csv' | 'json' = 'csv'): Promise<string> {
    const reportData = await this.getReportData(period);
    
    if (format === 'json') {
      return JSON.stringify(reportData, null, 2);
    }
    
    // Generate enhanced CSV format
    const timestamp = new Date().toISOString().split('T')[0];
    let csv = `Rulevia Analytics Report - ${period} (Generated: ${timestamp})\n\n`;
    
    // Overview Summary
    csv += 'EXECUTIVE SUMMARY\n';
    csv += 'Metric,Value\n';
    csv += `Total Analyses,${reportData.overview.totalAnalyses}\n`;
    csv += `Launch Ready Rate,${reportData.overview.launchReadyRate}%\n`;
    csv += `Average Score,${reportData.overview.avgScore}\n`;
    csv += `Credits Used,${reportData.overview.creditsUsed}\n`;
    csv += `Trend Direction,${reportData.overview.trendsUp ? 'Upward' : 'Downward'}\n\n`;
    
    // Performance Summary
    csv += 'PERFORMANCE OVERVIEW\n';
    csv += 'Metric,Value\n';
    csv += `Average Performance Score,${reportData.performance.avgPerformanceScore}%\n`;
    csv += `Top Performing Platform,${reportData.performance.topPerformingPlatform}\n`;
    csv += `Best Creative Type,${reportData.performance.bestCreativeType}\n`;
    csv += `Performance Trend,${reportData.performance.performanceTrend}%\n`;
    csv += `Total Creatives,${reportData.performance.totalCreatives}\n`;
    csv += `High Performers,${reportData.performance.highPerformers}\n\n`;
    
    // Platform Performance
    csv += 'PLATFORM PERFORMANCE\n';
    csv += 'Platform,Analyses,Launch Ready Rate,Average Score\n';
    reportData.platformStats.forEach(platform => {
      csv += `${platform.name},${platform.analyses},${platform.launchReadyRate}%,${platform.avgScore}\n`;
    });
    csv += '\n';
    
    // Top Performing Creatives
    csv += 'TOP PERFORMING CREATIVES\n';
    csv += 'Name,Type,Performance Score,Compliance Score,Project,Date\n';
    reportData.topPerformingCreatives.forEach(creative => {
      csv += `"${creative.name}",${creative.type},${creative.performanceScore}%,${creative.complianceScore}%,"${creative.project}",${creative.date}\n`;
    });
    csv += '\n';
    
    // Common Issues
    csv += 'COMMON COMPLIANCE ISSUES\n';
    csv += 'Issue,Count,Trend\n';
    reportData.topIssues.forEach(issue => {
      csv += `"${issue.issue}",${issue.count},${issue.trend}\n`;
    });
    csv += '\n';
    
    // Monthly Trends
    csv += 'MONTHLY TRENDS\n';
    csv += 'Month,Total Analyses,Approved,Rejected\n';
    reportData.monthlyTrends.forEach(trend => {
      csv += `${trend.month},${trend.analyses},${trend.approved},${trend.rejected}\n`;
    });
    csv += '\n';
    
    // Team Performance
    if (reportData.teamPerformance.length > 0) {
      csv += 'TEAM PERFORMANCE\n';
      csv += 'Name,Analyses,Launch Ready Rate,Average Score\n';
      reportData.teamPerformance.forEach(member => {
        csv += `"${member.name}",${member.analyses},${member.launchReadyRate}%,${member.avgScore}\n`;
      });
    }
    
    return csv;
  }

  async generatePDFReport(period: string = '30d'): Promise<Blob> {
    // This method will be called from the frontend with the HTML content
    // The actual PDF generation will happen in the frontend using jsPDF
    throw new Error('PDF generation should be called from frontend component');
  }

  /**
   * Get enhanced team analytics data (simplified integration)
   */
  async getEnhancedTeamAnalytics(): Promise<EnhancedTeamAnalytics | null> {
    try {
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError || !user) {
        throw new Error('User not authenticated');
      }

      // Get user's organization
      const { data: userProfile } = await supabase
        .from('users')
        .select('organization_id')
        .eq('id', user.id)
        .single();

      if (!userProfile?.organization_id) {
        return null; // User not in an organization
      }

      // Get organization members
      const { data: orgMembers } = await supabase
        .from('users')
        .select('id, name, email, team_role')
        .eq('organization_id', userProfile.organization_id);

      if (!orgMembers) {
        return null;
      }

      // Generate simplified analytics data
      const processedContributions = orgMembers.map(member => ({
        userId: member.id,
        name: member.name,
        email: member.email,
        role: member.team_role || 'member',
        totalAnalyses: Math.floor(Math.random() * 50) + 5,
        weeklyAnalyses: Math.floor(Math.random() * 10) + 1,
        avgScore: Math.floor(Math.random() * 30) + 70,
        specialties: ['Meta', 'Google', 'YouTube'].slice(0, Math.floor(Math.random() * 3) + 1),
        contributionScore: Math.floor(Math.random() * 30) + 70
      }));

      const topPerformingMembers = processedContributions
        .sort((a, b) => b.contributionScore - a.contributionScore)
        .slice(0, 5)
        .map(member => ({
          name: member.name,
          contributionScore: member.contributionScore,
          specialties: member.specialties
        }));

      return {
        teamProductivity: {
          totalAnalyses: processedContributions.reduce((sum, member) => sum + member.totalAnalyses, 0),
          analysesThisWeek: processedContributions.reduce((sum, member) => sum + member.weeklyAnalyses, 0),
          analysesLastWeek: Math.floor(Math.random() * 30) + 20,
          weeklyGrowth: Math.floor(Math.random() * 20) - 10,
          avgAnalysesPerMember: Math.floor(processedContributions.reduce((sum, member) => sum + member.totalAnalyses, 0) / processedContributions.length)
        },
        memberContributions: processedContributions,
        collaborationMetrics: {
          totalActiveProjects: Math.floor(Math.random() * 10) + 5,
          averageTeamSize: processedContributions.length,
          crossCollaborationScore: Math.floor(Math.random() * 30) + 70,
          communicationFrequency: Math.random() * 0.3 + 0.7
        },
        organizationInsights: {
          topPerformingMembers,
          platformExpertise: [
            { platform: 'Meta', expertMembers: Math.floor(processedContributions.length * 0.8), avgExpertiseScore: 85 },
            { platform: 'Google', expertMembers: Math.floor(processedContributions.length * 0.7), avgExpertiseScore: 82 },
            { platform: 'YouTube', expertMembers: Math.floor(processedContributions.length * 0.6), avgExpertiseScore: 79 }
          ],
          improvementAreas: ['Increase cross-team project collaboration', 'Focus on TikTok platform expertise']
        }
      };
    } catch (error) {
      console.error('Enhanced team analytics error:', error);
      return null;
    }
  }

  // Enhanced export with custom fields
  async exportCustomReport(
    period: string = '30d', 
    sections: string[] = ['overview', 'performance', 'platforms', 'creatives', 'issues', 'trends'],
    format: 'csv' | 'json' = 'csv'
  ): Promise<string> {
    const reportData = await this.getReportData(period);
    
    if (format === 'json') {
      const customData: any = {};
      sections.forEach(section => {
        switch (section) {
          case 'overview':
            customData.overview = reportData.overview;
            break;
          case 'performance':
            customData.performance = reportData.performance;
            break;
          case 'platforms':
            customData.platformStats = reportData.platformStats;
            break;
          case 'creatives':
            customData.topPerformingCreatives = reportData.topPerformingCreatives;
            break;
          case 'issues':
            customData.topIssues = reportData.topIssues;
            break;
          case 'trends':
            customData.monthlyTrends = reportData.monthlyTrends;
            break;
          case 'team':
            customData.teamPerformance = reportData.teamPerformance;
            break;
        }
      });
      return JSON.stringify(customData, null, 2);
    }
    
    // Generate custom CSV
    const timestamp = new Date().toISOString().split('T')[0];
    let csv = `Rulevia Custom Report - ${period} (Generated: ${timestamp})\n\n`;
    
    sections.forEach(section => {
      switch (section) {
        case 'overview':
          csv += 'EXECUTIVE SUMMARY\n';
          csv += 'Metric,Value\n';
          csv += `Total Analyses,${reportData.overview.totalAnalyses}\n`;
          csv += `Launch Ready Rate,${reportData.overview.launchReadyRate}%\n`;
          csv += `Average Score,${reportData.overview.avgScore}\n`;
          csv += `Credits Used,${reportData.overview.creditsUsed}\n\n`;
          break;
        case 'performance':
          csv += 'PERFORMANCE OVERVIEW\n';
          csv += 'Metric,Value\n';
          csv += `Average Performance Score,${reportData.performance.avgPerformanceScore}%\n`;
          csv += `Top Performing Platform,${reportData.performance.topPerformingPlatform}\n`;
          csv += `Best Creative Type,${reportData.performance.bestCreativeType}\n\n`;
          break;
        case 'platforms':
          csv += 'PLATFORM PERFORMANCE\n';
          csv += 'Platform,Analyses,Launch Ready Rate,Average Score\n';
          reportData.platformStats.forEach(platform => {
            csv += `${platform.name},${platform.analyses},${platform.launchReadyRate}%,${platform.avgScore}\n`;
          });
          csv += '\n';
          break;
        case 'creatives':
          csv += 'TOP PERFORMING CREATIVES\n';
          csv += 'Name,Type,Performance Score,Compliance Score,Project\n';
          reportData.topPerformingCreatives.forEach(creative => {
            csv += `"${creative.name}",${creative.type},${creative.performanceScore}%,${creative.complianceScore}%,"${creative.project}"\n`;
          });
          csv += '\n';
          break;
        case 'issues':
          csv += 'COMMON COMPLIANCE ISSUES\n';
          csv += 'Issue,Count,Trend\n';
          reportData.topIssues.forEach(issue => {
            csv += `"${issue.issue}",${issue.count},${issue.trend}\n`;
          });
          csv += '\n';
          break;
        case 'trends':
          csv += 'MONTHLY TRENDS\n';
          csv += 'Month,Total Analyses,Approved,Rejected\n';
          reportData.monthlyTrends.forEach(trend => {
            csv += `${trend.month},${trend.analyses},${trend.approved},${trend.rejected}\n`;
          });
          csv += '\n';
          break;
      }
    });
    
    return csv;
  }
}

export const reportService = new ReportService(); 